//frontend/src/pages/company/InventoryOutbound.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import CompanyLayout from '../../components/layout/CompanyLayout';
import { shopAPI, productAPI, outboundAPI, warehouseAPI } from '../../services/api';

const InventoryOutbound = () => {
  const { user, hasPermission } = useAuth();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('create'); // 'create' | 'history'
  
  // CREATE TAB - State
  const [shops, setShops] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [items, setItems] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  // HISTORY TAB - State
  const [outbounds, setOutbounds] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState({
    period: 'today',
    shop_id: '',
    warehouse_id: '',
    start_date: '',
    end_date: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });
  const [grandTotals, setGrandTotals] = useState({
    totalOutbounds: 0,
    totalItems: 0,
    totalQuantity: 0,
    totalAmount: 0
  });
  const [selectedOutbound, setSelectedOutbound] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // EDIT/DELETE State
  const [editingOutbound, setEditingOutbound] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchShops();
    fetchWarehouses();
    if (activeTab === 'history') {
      fetchOutbounds();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchOutbounds();
    }
  }, [filters, pagination.currentPage]);

  const fetchShops = async () => {
    try {
      const response = await shopAPI.getAll();
      setShops(response.data.data || []);
    } catch (error) {
      console.error('Fetch shops error:', error);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await warehouseAPI.getAll();
      setWarehouses(response.data.data || []);
    } catch (error) {
      console.error('Fetch warehouses error:', error);
    }
  };

  const fetchProducts = async (shopId) => {
    try {
      if (!shopId) {
        setProducts([]);
        return;
      }
      const response = await productAPI.getAll({ shop_id: shopId, limit: 1000 });
      const productsWithStock = (response.data.data || []).filter(p => p.stock_quantity > 0);
      setProducts(productsWithStock);
    } catch (error) {
      console.error('Fetch products error:', error);
    }
  };

  const getDateRange = (period) => {
    const today = new Date();
    let startDate, endDate;

    switch (period) {
      case 'today':
        startDate = endDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = yesterday.toISOString().split('T')[0];
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = weekAgo.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        startDate = monthAgo.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'custom':
        startDate = filters.start_date;
        endDate = filters.end_date;
        break;
      default:
        startDate = endDate = '';
    }

    return { startDate, endDate };
  };

  const fetchOutbounds = async () => {
    try {
      setLoadingHistory(true);
      const { startDate, endDate } = getDateRange(filters.period);
      
      const params = {
        page: pagination.currentPage,
        limit: 20,
        ...(filters.shop_id && { shop_id: filters.shop_id }),
        ...(filters.warehouse_id && { warehouse_id: filters.warehouse_id }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate })
      };

      const response = await outboundAPI.getAllOutbounds(params);
      setOutbounds(response.data.data || []);
      setPagination({
        currentPage: response.data.pagination.currentPage,
        totalPages: response.data.pagination.totalPages,
        totalItems: response.data.pagination.totalItems
      });
      setGrandTotals(response.data.summary || {
        totalOutbounds: 0,
        totalItems: 0,
        totalQuantity: 0,
        totalAmount: 0
      });
    } catch (error) {
      console.error('Fetch outbounds error:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchOutboundDetail = async (id) => {
    try {
      const response = await outboundAPI.getOutboundById(id);
      setSelectedOutbound(response.data.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Fetch outbound detail error:', error);
      alert('Дэлгэрэнгүй мэдээлэл татахад алдаа гарлаа');
    }
  };

  const handleShopChange = (shopId) => {
    const shop = shops.find(s => s.id === parseInt(shopId));
    setSelectedShop(shop);
    setItems([]);
    fetchProducts(shopId);
  };

  const handleAddProduct = (product) => {
    const exists = items.find(item => item.product_id === product.id);
    if (exists) {
      alert('Энэ бараа аль хэдийн нэмэгдсэн байна');
      return;
    }

    if (!product.stock_quantity || product.stock_quantity <= 0) {
      alert(`"${product.product_name}" барааны үлдэгдэл байхгүй байна`);
      return;
    }

    setItems([...items, {
      product_id: product.id,
      product_name: product.product_name,
      sku: product.sku,
      stock_quantity: product.stock_quantity,
      base_price: product.base_price || 0,
      quantity: 1,
      unit_price: product.base_price || 0
    }]);
    setShowProductModal(false);
    setSearchTerm('');
  };

  const handleUpdateItem = (index, field, value) => {
    const newItems = [...items];
    
    if (field === 'quantity') {
      const maxStock = newItems[index].stock_quantity;
      if (parseInt(value) > maxStock) {
        alert(`Үлдэгдэл хүрэлцэхгүй байна (Үлдэгдэл: ${maxStock})`);
        return;
      }
    }
    
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      if (!selectedShop) {
        alert('Онлайн шоп сонгоно уу');
        return;
      }

      if (!selectedShop.warehouse_id) {
        alert('Энэ шопод агуулах холбогдоогүй байна');
        return;
      }

      if (!recipientName.trim()) {
        alert('Авагчийн нэр оруулна уу');
        return;
      }

      if (!recipientPhone.trim()) {
        alert('Авагчийн утас оруулна уу');
        return;
      }

      if (items.length === 0) {
        alert('Бараа нэмнэ үү');
        return;
      }

      for (const item of items) {
        if (!item.quantity || parseInt(item.quantity) <= 0) {
          alert(`"${item.product_name}" барааны тоо ширхэг 0-с их байх ёстой`);
          return;
        }
        if (item.quantity > item.stock_quantity) {
          alert(`"${item.product_name}" барааны үлдэгдэл хүрэлцэхгүй байна`);
          return;
        }
        if (!item.unit_price || parseFloat(item.unit_price) <= 0) {
          alert(`"${item.product_name}" барааны үнэ 0-с их байх ёстой`);
          return;
        }
      }

      setLoading(true);

      const outboundData = {
        shop_id: selectedShop.id,
        warehouse_id: selectedShop.warehouse_id,
        recipient_name: recipientName,
        recipient_phone: recipientPhone,
        notes: notes,
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: parseInt(item.quantity),
          unit_price: parseFloat(item.unit_price)
        }))
      };

      await outboundAPI.createOutbound(outboundData);
      
      alert('Зарлага амжилттай бүртгэгдлээ!');
      
      setSelectedShop(null);
      setItems([]);
      setRecipientName('');
      setRecipientPhone('');
      setNotes('');
      
      setActiveTab('history');
      
    } catch (error) {
      console.error('Create outbound error:', error);
      alert(error.response?.data?.message || 'Зарлага бүртгэхэд алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOutbound = async (id) => {
    if (!window.confirm('Зарлага устгах уу? Энэ үйлдлийг буцаах боломжгүй!')) {
      return;
    }

    try {
      await outboundAPI.deleteOutbound(id);
      alert('Зарлага устгагдлаа');
      setShowDetailModal(false);
      setSelectedOutbound(null);
      fetchOutbounds();
    } catch (error) {
      console.error('Delete outbound error:', error);
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  const handleEditOutbound = (outbound) => {
    setEditingOutbound(outbound);
    setShowEditModal(true);
  };

  const handleUpdateOutbound = async () => {
    try {
      if (!editingOutbound) return;

      await outboundAPI.updateOutbound(editingOutbound.id, {
        notes: editingOutbound.notes,
        recipient_name: editingOutbound.recipient_name,
        recipient_phone: editingOutbound.recipient_phone
      });

      alert('Зарлага шинэчлэгдлээ');
      setShowEditModal(false);
      setEditingOutbound(null);
      fetchOutboundDetail(editingOutbound.id);
      fetchOutbounds();
    } catch (error) {
      console.error('Update outbound error:', error);
      alert(error.response?.data?.message || 'Засахад алдаа гарлаа');
    }
  };

  const getTotalAmount = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const getTotalQuantity = () => {
    return items.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
  };

  const filteredProducts = products.filter(p => 
    p.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPeriodLabel = (period) => {
    const labels = {
      today: 'Өнөөдөр',
      yesterday: 'Өчигдөр',
      week: '7 хоног',
      month: 'Энэ сар',
      custom: 'Өөрөө сонгох'
    };
    return labels[period] || 'Өнөөдөр';
  };

  return (
    <CompanyLayout>
      <div className="space-y-4">
        {/* HEADER */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center">
              <span className="text-3xl">📤</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Зарлага удирдах</h1>
              <p className="text-sm text-gray-600 mt-1">Агуулахаас бараа зарлагадах болон түүх харах</p>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              {hasPermission('inventory.issue') && (
                <button
                  onClick={() => setActiveTab('create')}
                  className={`flex-1 px-6 py-4 text-sm font-semibold transition ${
                    activeTab === 'create'
                      ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  📤 Зарлага бүртгэх
                </button>
                )}
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition ${
                  activeTab === 'history'
                    ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                📋 Зарлагын түүх
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* CREATE TAB */}
            {activeTab === 'create' && (
              <div className="space-y-6">
                {/* SHOP SELECTION */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Онлайн шоп *
                  </label>
                  <select
                    value={selectedShop?.id || ''}
                    onChange={(e) => handleShopChange(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  >
                    <option value="">-- Шоп сонгох --</option>
                    {shops.map((shop) => (
                      <option key={shop.id} value={shop.id}>
                        {shop.shop_name}
                      </option>
                    ))}
                  </select>
                  
                  {selectedShop && selectedShop.warehouse && (
                    <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🏭</span>
                        <div className="text-sm">
                          <span className="text-gray-600">Агуулах:</span>{' '}
                          <span className="font-semibold text-gray-900">{selectedShop.warehouse.warehouse_name}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* RECIPIENT INFO */}
                {selectedShop && (
                  <>
                    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">👤 Авагчийн мэдээлэл</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Нэр *
                          </label>
                          <input
                            type="text"
                            value={recipientName}
                            onChange={(e) => setRecipientName(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                            placeholder="Авагчийн нэр"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Утас *
                          </label>
                          <input
                            type="tel"
                            value={recipientPhone}
                            onChange={(e) => setRecipientPhone(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                            placeholder="99119911"
                          />
                        </div>
                      </div>
                    </div>

                    {/* ITEMS LIST */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      <div className="p-5 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h2 className="text-lg font-semibold text-gray-900">Барааны жагсаалт</h2>
                            <span className="px-2.5 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                              {items.length}
                            </span>
                          </div>
                          <button
                            onClick={() => setShowProductModal(true)}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            <span>Бараа нэмэх</span>
                          </button>
                        </div>
                      </div>

                      {items.length === 0 ? (
                        <div className="text-center py-12 px-6">
                          <div className="text-5xl mb-3">📦</div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">Бараа нэмнэ үү</h3>
                          <p className="text-sm text-gray-600 mb-4">Зарлагадах барааг сонгоно уу</p>
                          <button
                            onClick={() => setShowProductModal(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition"
                          >
                            <span>+</span>
                            <span>Бараа нэмэх</span>
                          </button>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {items.map((item, index) => (
                            <div key={index} className="p-4 hover:bg-gray-50 transition group">
                              <div className="grid grid-cols-12 gap-3 items-center">
                                <div className="col-span-4">
                                  <div className="flex items-start gap-2">
                                    <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                      <span className="text-lg">📦</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900 truncate text-sm">{item.product_name}</div>
                                      <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-xs text-gray-500">SKU: {item.sku}</span>
                                        <span className="text-xs font-medium text-orange-600">
                                          Үлдэгдэл: {item.stock_quantity}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="col-span-2">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Тоо ширхэг</label>
                                  <input
                                    type="number"
                                    min="1"
                                    max={item.stock_quantity}
                                    value={item.quantity}
                                    onChange={(e) => handleUpdateItem(index, 'quantity', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-sm text-center"
                                  />
                                </div>

                                <div className="col-span-3">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Нэгж үнэ</label>
                                  <div className="relative">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={item.unit_price}
                                      onChange={(e) => handleUpdateItem(index, 'unit_price', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-sm"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₮</span>
                                  </div>
                                </div>

                                <div className="col-span-2">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Дүн</label>
                                  <div className="text-sm font-semibold text-orange-600">
                                    {(item.quantity * item.unit_price).toLocaleString()}₮
                                  </div>
                                </div>

                                <div className="col-span-1 flex justify-end">
                                  <button
                                    onClick={() => handleRemoveItem(index)}
                                    className="w-8 h-8 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition opacity-0 group-hover:opacity-100 flex items-center justify-center"
                                    title="Устгах"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {items.length > 0 && (
                        <div className="bg-orange-50 border-t border-orange-200 p-4">
                          <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-3 bg-white rounded-lg border border-orange-200">
                              <div className="text-xs font-medium text-gray-600 mb-1">Нийт бараа</div>
                              <div className="text-2xl font-bold text-orange-600">{items.length}</div>
                            </div>
                            <div className="text-center p-3 bg-white rounded-lg border border-orange-200">
                              <div className="text-xs font-medium text-gray-600 mb-1">Нийт ширхэг</div>
                              <div className="text-2xl font-bold text-orange-600">{getTotalQuantity()}</div>
                            </div>
                            <div className="text-center p-3 bg-white rounded-lg border border-orange-200">
                              <div className="text-xs font-medium text-gray-600 mb-1">Нийт дүн</div>
                              <div className="text-2xl font-bold text-orange-600">{getTotalAmount().toLocaleString()}₮</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* NOTES */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Тэмдэглэл
                      </label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition resize-none text-sm"
                        rows="3"
                        placeholder="Зарлагын тэмдэглэл..."
                      ></textarea>
                    </div>

                    {/* ACTIONS */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setSelectedShop(null);
                          setItems([]);
                          setRecipientName('');
                          setRecipientPhone('');
                          setNotes('');
                        }}
                        className="flex-1 px-5 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
                      >
                        Болих
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={loading || items.length === 0 || !recipientName || !recipientPhone}
                        className="flex-1 px-5 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {loading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Хадгалж байна...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>Зарлага бүртгэх</span>
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* HISTORY TAB */}
            {activeTab === 'history' && (
              <div className="space-y-6">
                {/* FILTERS */}
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">🔍 Шүүлтүүр</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">Хугацаа</label>
                      <select
                        value={filters.period}
                        onChange={(e) => setFilters({ ...filters, period: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-sm"
                      >
                        <option value="today">Өнөөдөр</option>
                        <option value="yesterday">Өчигдөр</option>
                        <option value="week">7 хоног</option>
                        <option value="month">Энэ сар</option>
                        <option value="custom">Өөрөө сонгох</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">Онлайн шоп</label>
                      <select
                        value={filters.shop_id}
                        onChange={(e) => setFilters({ ...filters, shop_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-sm"
                      >
                        <option value="">Бүгд</option>
                        {shops.map((shop) => (
                          <option key={shop.id} value={shop.id}>{shop.shop_name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">Агуулах</label>
                      <select
                        value={filters.warehouse_id}
                        onChange={(e) => setFilters({ ...filters, warehouse_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-sm"
                      >
                        <option value="">Бүгд</option>
                        {warehouses.map((warehouse) => (
                          <option key={warehouse.id} value={warehouse.id}>{warehouse.warehouse_name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">Хайлт</label>
                      <input
                        type="text"
                        placeholder="Receipt дугаар..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-sm"
                      />
                    </div>
                  </div>

                  {filters.period === 'custom' && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">Эхлэх огноо</label>
                        <input
                          type="date"
                          value={filters.start_date}
                          onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">Дуусах огноо</label>
                        <input
                          type="date"
                          value={filters.end_date}
                          onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* OUTBOUNDS LIST */}
                {loadingHistory ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Уншиж байна...</p>
                  </div>
                ) : outbounds.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📋</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Зарлага олдсонгүй</h3>
                    <p className="text-gray-600">Сонгосон хугацаанд зарлага бүртгэгдээгүй байна</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      {outbounds.map((outbound) => (
                      <div key={outbound.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Header with Badge */}
                            <div className="flex items-center gap-3 mb-3">
                              <h4 className="text-lg font-bold text-gray-900">{outbound.receipt_number}</h4>
                              {outbound.source_type !== 'manual' ? (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                                  Auto (Хүргэлт)
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-semibold">
                                  Manual
                                </span>
                              )}
                            </div>

                            {/* Delivery Number (auto only) */}
                            {outbound.delivery_number && (
                              <div className="mb-3 text-sm">
                                <span className="text-gray-600">🚚 Хүргэлтийн дугаар:</span>{' '}
                                <span className="font-semibold text-blue-600">{outbound.delivery_number}</span>
                              </div>
                            )}
                            
                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">📅 Огноо:</span>{' '}
                                <span className="font-medium text-gray-900">
                                  {new Date(outbound.receipt_date).toLocaleDateString('mn-MN')} {new Date(outbound.created_at).toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">🛍️ Шоп:</span>{' '}
                                <span className="font-medium text-gray-900">{outbound.shop?.shop_name}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">🏭 Агуулах:</span>{' '}
                                <span className="font-medium text-gray-900">{outbound.warehouse?.warehouse_name}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">📦 Бараа:</span>{' '}
                                <span className="font-medium text-gray-900">
                                  {outbound.summary?.totalItems} төрөл ({outbound.summary?.totalQuantity} ш)
                                </span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-gray-600">👤 Авагч:</span>{' '}
                                <span className="font-medium text-gray-900">
                                  {outbound.recipient_name} ({outbound.recipient_phone})
                                </span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-gray-600">👤 Бүртгэсэн:</span>{' '}
                                <span className="font-medium text-gray-900">
                                  {outbound.creator?.full_name || outbound.creator?.username || '-'}
                                </span>
                              </div>
                            </div>

                            {/* Footer */}
                            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                              <div className="text-2xl font-bold text-orange-600">
                                {outbound.summary?.totalAmount?.toLocaleString()}₮
                              </div>
                              <button
                                onClick={() => fetchOutboundDetail(outbound.id)}
                                className="px-4 py-2 bg-orange-50 text-orange-600 rounded-lg font-medium hover:bg-orange-100 transition flex items-center gap-2 text-sm"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span>Дэлгэрэнгүй</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>

                    {/* SUMMARY */}
                    <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-orange-900 mb-3">
                        📊 НИЙТ ДҮНГИЙН МЭДЭЭЛЭЛ ({getPeriodLabel(filters.period)})
                      </h3>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-orange-600 font-medium">Нийт зарлага:</div>
                          <div className="text-2xl font-bold text-orange-900">{grandTotals.totalOutbounds}</div>
                        </div>
                        <div>
                          <div className="text-orange-600 font-medium">Нийт бараа:</div>
                          <div className="text-2xl font-bold text-orange-900">{grandTotals.totalItems} төрөл</div>
                        </div>
                        <div>
                          <div className="text-orange-600 font-medium">Нийт ширхэг:</div>
                          <div className="text-2xl font-bold text-orange-900">{grandTotals.totalQuantity} ш</div>
                        </div>
                        <div>
                          <div className="text-orange-600 font-medium">Нийт дүн:</div>
                          <div className="text-2xl font-bold text-orange-900">{grandTotals.totalAmount.toLocaleString()}₮</div>
                        </div>
                      </div>
                    </div>

                    {/* PAGINATION */}
                    {pagination.totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setPagination({ ...pagination, currentPage: pagination.currentPage - 1 })}
                          disabled={pagination.currentPage === 1}
                          className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ◀ Өмнөх
                        </button>
                        
                        <div className="flex items-center gap-2">
                          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => setPagination({ ...pagination, currentPage: page })}
                              className={`w-10 h-10 rounded-lg font-medium transition ${
                                page === pagination.currentPage
                                  ? 'bg-orange-600 text-white'
                                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                        </div>

                        <button
                          onClick={() => setPagination({ ...pagination, currentPage: pagination.currentPage + 1 })}
                          disabled={pagination.currentPage === pagination.totalPages}
                          className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Дараах ▶
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PRODUCT SELECTION MODAL */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col shadow-xl">
            <div className="p-5 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Бараа сонгох
                </h3>
                <button
                  onClick={() => {
                    setShowProductModal(false);
                    setSearchTerm('');
                  }}
                  className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center justify-center"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Бараа хайх (нэр, SKU)..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-sm"
                  autoFocus
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {filteredProducts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-5xl mb-3">🔍</div>
                  <p className="text-sm text-gray-600">
                    {products.length === 0 ? 'Үлдэгдэлтэй бараа байхгүй' : 'Бараа олдсонгүй'}
                  </p>
                </div>
              ) : (
                <div className="grid gap-2">
                  {filteredProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleAddProduct(product)}
                      className="w-full p-3 bg-white border border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition text-left group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center group-hover:scale-105 transition">
                          <span className="text-lg">📦</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate text-sm">{product.product_name}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-500">SKU: {product.sku}</span>
                            <span className="text-xs font-semibold text-orange-600">
                              Үлдэгдэл: {product.stock_quantity}
                            </span>
                            {product.base_price && (
                              <span className="text-xs font-semibold text-orange-600">
                                {product.base_price.toLocaleString()}₮
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="w-7 h-7 bg-orange-600 text-white rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {showDetailModal && selectedOutbound && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-xl">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-orange-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">📤 Зарлагын дэлгэрэнгүй</h3>
                  <p className="text-sm text-gray-600 mt-1">{selectedOutbound.receipt_number}</p>
                </div>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedOutbound(null);
                  }}
                  className="w-10 h-10 bg-white hover:bg-gray-100 rounded-lg transition flex items-center justify-center shadow"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">ҮНДСЭН МЭДЭЭЛЭЛ</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Баримтын дугаар:</span>
                    <div className="font-semibold text-gray-900 mt-1">{selectedOutbound.receipt_number}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Огноо:</span>
                    <div className="font-semibold text-gray-900 mt-1">
                      {new Date(selectedOutbound.receipt_date).toLocaleDateString('mn-MN')} {new Date(selectedOutbound.created_at).toLocaleTimeString('mn-MN')}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Онлайн шоп:</span>
                    <div className="font-semibold text-gray-900 mt-1">{selectedOutbound.shop?.shop_name}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Агуулах:</span>
                    <div className="font-semibold text-gray-900 mt-1">{selectedOutbound.warehouse?.warehouse_name}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Авагч:</span>
                    <div className="font-semibold text-gray-900 mt-1">
                      {selectedOutbound.recipient_name}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Утас:</span>
                    <div className="font-semibold text-gray-900 mt-1">
                      {selectedOutbound.recipient_phone}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Бүртгэсэн:</span>
                    <div className="font-semibold text-gray-900 mt-1">
                      {selectedOutbound.creator?.full_name || selectedOutbound.creator?.username || '-'}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Тэмдэглэл:</span>
                    <div className="font-medium text-gray-900 mt-1">{selectedOutbound.notes || '-'}</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200">
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                  <h4 className="font-semibold text-gray-900">БАРААНЫ ЖАГСААЛТ</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {selectedOutbound.items?.map((item, index) => (
                    <div key={item.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">
                            {index + 1}. {item.product?.product_name}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            SKU: {item.product?.sku}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">
                            {item.quantity} ш × {parseFloat(item.unit_price).toLocaleString()}₮
                          </div>
                          <div className="text-lg font-bold text-orange-600 mt-1">
                            = {(item.quantity * parseFloat(item.unit_price)).toLocaleString()}₮
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-4 bg-orange-50 border-t-2 border-orange-200">
                  <div className="flex items-center justify-between text-lg font-bold">
                    <span className="text-gray-900">
                      НИЙТ: {selectedOutbound.items?.length} төрөл × {selectedOutbound.items?.reduce((sum, item) => sum + item.quantity, 0)} ширхэг
                    </span>
                    <span className="text-orange-600 text-2xl">
                      {selectedOutbound.items?.reduce((sum, item) => sum + (item.quantity * parseFloat(item.unit_price)), 0).toLocaleString()}₮
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex gap-3">
                {hasPermission('inventory.issue') && (
                  <>
                    <button
                      onClick={() => handleEditOutbound(selectedOutbound)}
                      className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Засах</span>
                    </button>
                    <button
                      onClick={() => handleDeleteOutbound(selectedOutbound.id)}
                      className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Устгах</span>
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedOutbound(null);
                  }}
                  className={`${hasPermission('inventory.issue') ? 'flex-1' : 'w-full'} px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition`}
                >
                  Хаах
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && editingOutbound && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Зарлага засах</h3>
              <p className="text-sm text-gray-600 mt-1">{editingOutbound.receipt_number}</p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Авагчийн нэр
                </label>
                <input
                  type="text"
                  value={editingOutbound.recipient_name || ''}
                  onChange={(e) => setEditingOutbound({ ...editingOutbound, recipient_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Авагчийн утас
                </label>
                <input
                  type="tel"
                  value={editingOutbound.recipient_phone || ''}
                  onChange={(e) => setEditingOutbound({ ...editingOutbound, recipient_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Тэмдэглэл
                </label>
                <textarea
                  value={editingOutbound.notes || ''}
                  onChange={(e) => setEditingOutbound({ ...editingOutbound, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition resize-none"
                  rows="3"
                  placeholder="Тэмдэглэл..."
                ></textarea>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingOutbound(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Болих
              </button>
              <button
                onClick={handleUpdateOutbound}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
              >
                Хадгалах
              </button>
            </div>
          </div>
        </div>
      )}
    </CompanyLayout>
  );
};

export default InventoryOutbound;